import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import DoctorShell from "@/components/DoctorShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { inr } from "@/lib/constants";
import { CheckCircle2, AlertTriangle, ArrowUpRight, RefreshCw } from "lucide-react";

const AMC_BADGE = {
    active: { cls: "bg-green-100 text-[#15803D]", label: "Active" },
    due: { cls: "bg-yellow-100 text-yellow-900", label: "Renewal due" },
    overdue: { cls: "bg-red-100 text-[#DC2626]", label: "Overdue" },
};

export default function Billing() {
    const [data, setData] = useState(null);
    const [packages, setPackages] = useState([]);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [doctor, setDoctor] = useState(null);
    const nav = useNavigate();

    async function load() {
        try {
            const [b, p] = await Promise.all([api.get("/me/billing"), api.get("/packages")]);
            setData(b.data);
            setPackages(p.data);
            setDoctor(b.data.doctor);
        } catch (e) {
            toast.error(errMsg(e, "Failed to load"));
        }
    }
    useEffect(() => { load(); }, []);

    async function previewUpgrade(code) {
        try {
            const r = await api.get(`/me/billing/upgrade-preview/${code}`);
            setPreview(r.data);
        } catch (e) {
            toast.error(errMsg(e, "Failed to preview upgrade"));
        }
    }

    async function doUpgrade() {
        if (!preview) return;
        setBusy(true);
        try {
            const o = await api.post("/me/billing/upgrade", { new_package_code: preview.to_package.code });
            nav(`/doctor/checkout/${o.data.id}`);
        } catch (e) {
            toast.error(errMsg(e, "Failed to create upgrade order"));
        } finally {
            setBusy(false);
        }
    }
    async function renewAMC() {
        setBusy(true);
        try {
            const o = await api.post("/me/billing/amc/renew");
            nav(`/doctor/checkout/${o.data.id}`);
        } catch (e) {
            toast.error(errMsg(e, "Failed to create AMC order"));
        } finally {
            setBusy(false);
        }
    }

    if (!data) return <DoctorShell><div className="text-[#17212B]/60">Loading billing…</div></DoctorShell>;

    const pkg = data.package;
    const sub = data.subscription;
    const amcBadge = AMC_BADGE[data.amc_status] || AMC_BADGE.active;
    const higherTiers = packages.filter((p) => pkg && p.tier_level > pkg.tier_level);

    return (
        <DoctorShell doctor={doctor}>
            <div className="mb-6" data-testid="billing-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Finance</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Billing & AMC</h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <Card className="border-[#123B5D]/10" data-testid="billing-package">
                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">Package</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-lg font-bold text-[#123B5D]">{pkg?.name || "—"}</div>
                        <div className="text-xs text-[#17212B]/60 mt-1">One-time {pkg && inr(pkg.price_inr)} · AMC {pkg && inr(pkg.amc_inr)}/yr</div>
                    </CardContent>
                </Card>
                <Card className="border-[#123B5D]/10" data-testid="billing-paid">
                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">Paid so far</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-lg font-bold text-[#123B5D]">{inr(data.amount_paid_total_inr)}</div>
                        <div className="text-xs text-[#17212B]/60 mt-1">Doctor state: {data.doctor?.payment_state}</div>
                    </CardContent>
                </Card>
                <Card className="border-[#123B5D]/10" data-testid="billing-amc">
                    <CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">AMC status</CardTitle></CardHeader>
                    <CardContent>
                        <span className={"ddi-badge " + amcBadge.cls} data-testid="amc-badge">{amcBadge.label}</span>
                        <div className="text-xs text-[#17212B]/60 mt-2">
                            {sub?.next_renewal_at ? `Renews ${sub.next_renewal_at.slice(0, 10)}` : "No subscription yet"}
                            {data.days_to_renewal !== null && <span className="ml-1">({data.days_to_renewal}d)</span>}
                        </div>
                        {(data.amc_status === "due" || data.amc_status === "overdue") && sub && (
                            <Button size="sm" onClick={renewAMC} disabled={busy} className="mt-2 bg-[#0F766E] hover:bg-[#0b5f5a] text-white" data-testid="amc-renew">
                                <RefreshCw size={14} className="mr-1" /> Renew AMC
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>

            {higherTiers.length > 0 && (
                <Card className="border-[#123B5D]/10 mb-6" data-testid="upgrade-section">
                    <CardHeader><CardTitle className="text-base flex items-center gap-2"><ArrowUpRight size={16} /> Upgrade package</CardTitle></CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2 mb-3">
                            {higherTiers.map((p) => (
                                <button
                                    key={p.code}
                                    onClick={() => previewUpgrade(p.code)}
                                    className={"px-4 py-2 rounded-lg border-2 transition text-sm " + (preview?.to_package?.code === p.code ? "border-[#0F766E] bg-[#0F766E]/10 text-[#0F766E]" : "border-[#123B5D]/10 hover:border-[#0F766E]")}
                                    data-testid={`upgrade-target-${p.code}`}
                                >
                                    {p.name} · {inr(p.price_inr)}
                                </button>
                            ))}
                        </div>
                        {preview && (
                            <Card className="bg-[#0F766E]/5 border-[#0F766E]/30">
                                <CardContent className="pt-4">
                                    <div className="text-sm font-semibold text-[#123B5D] mb-2">Proration preview: {preview.from_package.name} → {preview.to_package.name}</div>
                                    <table className="text-sm w-full">
                                        <tbody>
                                            <tr><td className="py-1">New package price</td><td className="text-right font-semibold">{inr(preview.to_package.price_inr)}</td></tr>
                                            <tr><td className="py-1">Paid so far</td><td className="text-right">{inr(preview.paid_so_far_inr)}</td></tr>
                                            <tr><td className="py-1">Credit applied ({preview.credit_pct}%)</td><td className="text-right">− {inr(preview.credit_inr)}</td></tr>
                                            <tr className="border-t border-[#123B5D]/10"><td className="py-2 font-bold text-[#123B5D]">Upgrade charge now</td><td className="text-right font-bold text-[#123B5D]" data-testid="upgrade-charge">{inr(preview.upgrade_charge_inr)}</td></tr>
                                            <tr><td className="py-1 text-xs text-[#17212B]/60">AMC difference (from next renewal)</td><td className="text-right text-xs">{inr(preview.amc_difference_inr_next_renewal)}/yr</td></tr>
                                        </tbody>
                                    </table>
                                    <Button onClick={doUpgrade} disabled={busy} className="mt-4 bg-[#123B5D] hover:bg-[#0b2740] text-white" data-testid="upgrade-pay">
                                        Pay {inr(preview.upgrade_charge_inr)} to upgrade
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </CardContent>
                </Card>
            )}

            <Card className="border-[#123B5D]/10 mb-6">
                <CardHeader><CardTitle className="text-base">Invoices</CardTitle></CardHeader>
                <CardContent>
                    <table className="w-full text-sm">
                        <thead className="text-left border-b border-[#123B5D]/10">
                            <tr><th className="py-2">#</th><th>Installment</th><th>Package</th><th>Total</th><th>Issued</th></tr>
                        </thead>
                        <tbody>
                            {data.invoices.map((i) => (
                                <tr key={i.id} className="border-b border-[#123B5D]/5" data-testid={`invoice-${i.id}`}>
                                    <td className="py-2 font-mono text-xs">{i.invoice_number}</td>
                                    <td>{i.installment}</td>
                                    <td>{i.package_code}</td>
                                    <td className="font-semibold">{inr(i.total_inr)}</td>
                                    <td className="text-xs">{i.issued_at?.slice(0, 10)}</td>
                                </tr>
                            ))}
                            {data.invoices.length === 0 && <tr><td colSpan={5} className="text-[#17212B]/60 py-4 text-center">No invoices yet.</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            <Card className="border-[#123B5D]/10">
                <CardHeader><CardTitle className="text-base">Payment history</CardTitle></CardHeader>
                <CardContent>
                    <table className="w-full text-sm">
                        <thead className="text-left border-b border-[#123B5D]/10">
                            <tr><th className="py-2">Amount</th><th>Installment</th><th>Status</th><th>Gateway</th><th>Date</th></tr>
                        </thead>
                        <tbody>
                            {data.payments.map((p) => (
                                <tr key={p.id} className="border-b border-[#123B5D]/5" data-testid={`payment-${p.id}`}>
                                    <td className="py-2 font-semibold">{inr(p.amount_inr)}</td>
                                    <td>{p.installment}</td>
                                    <td><span className="ddi-badge bg-green-100 text-[#15803D] text-[10px]"><CheckCircle2 size={10} className="inline mr-1" />{p.status}</span></td>
                                    <td className="text-xs">{p.gateway}</td>
                                    <td className="text-xs">{p.created_at?.slice(0, 10)}</td>
                                </tr>
                            ))}
                            {data.payments.length === 0 && <tr><td colSpan={5} className="text-[#17212B]/60 py-4 text-center">No payments yet.</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </DoctorShell>
    );
}
